import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, MenuItem, Divider } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import GroupsIcon from "@mui/icons-material/Groups";
import InsightsIcon from "@mui/icons-material/Insights";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CampaignIcon from "@mui/icons-material/Campaign";
import LocalLibraryOutlinedIcon from "@mui/icons-material/LocalLibraryOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import { useThemeMode } from "../../contexts/ThemeModeContext";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useLogoutMutation } from "../../api/authApi";
import { useGetLibraryAccessQuery } from "../../api/libraryApi";
import BrandLockup from "../../components/brand/BrandLockup";
import amblem from "../../assets/img/amblem.png";
import amblemBeyaz from "../../assets/img/terakki-amblem-beyaz.png";

// Üye olunan gruplar /uygulama/gruplar altında; /uygulama/gruplarim yönetilen
// grupların sayfası (eski üye detay bağlantıları çalışsın diye "end" ile).
// Mobil yan menüdeki gibi başlıklı gruplar; Terakkim başlıksız en üstte.
const sections = [
  { key: null, items: [{ to: "/uygulama/profil", key: "nav_profile", Icon: CalendarMonthIcon }] },
  {
    key: "nav_section_tracking",
    items: [
      { to: "/uygulama/istatistik", key: "nav_personal_stats", Icon: InsightsIcon },
      { to: "/uygulama/unvanlar", key: "nav_titles", Icon: WorkspacePremiumOutlinedIcon },
      // Müzakere kütüphanesi yalnız en az bir grubun yöneticisine (mobil yan menüdeki gibi).
      { to: "/uygulama/kutuphane", key: "nav_library", Icon: LocalLibraryOutlinedIcon, library: true },
    ],
  },
  {
    key: "nav_section_manage",
    items: [
      { to: "/uygulama/gruplarim", key: "nav_groups", Icon: GroupsIcon, end: true },
      { to: "/uygulama/hatim-yonetim", key: "nav_hatim_management", Icon: MenuBookIcon },
    ],
  },
  {
    key: "nav_section_community",
    items: [{ to: "/uygulama/duyurular", key: "nav_notifications", Icon: CampaignIcon }],
  },
];

export default function SideNav() {
  const { t } = useScriptMode();
  const { user, clearSession } = useAuth();
  const [logout] = useLogoutMutation();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);
  const { mode, toggle } = useThemeMode();
  const dark = mode === "dark";
  // Hafif erişim bilgisi ({ canUse }); hata ya da bilinmezken giriş gizli.
  const { data: accessData } = useGetLibraryAccessQuery(undefined, { skip: !user });
  const canUseLibrary = Boolean(accessData?.data?.canUse);
  const visibleSections = sections
    .map((section) => ({ ...section, items: section.items.filter((item) => !item.library || canUseLibrary) }))
    .filter((section) => section.items.length > 0);

  const handleLogout = async () => {
    setAnchor(null);
    try {
      await logout().unwrap();
    } catch {
      // backend logout başarısız olsa da yerel oturumu düşürüyoruz
    }
    clearSession();
    navigate("/giris", { replace: true });
  };

  const fullName = user ? `${user.name || ""} ${user.surname || ""}`.trim() : "";
  const displayName = fullName || user?.email || "";
  const initial = (user?.name || user?.email || "?").slice(0, 1).toUpperCase();

  return (
    // Koyu temada ray saydam: arkadaki koyu geçiş görünür, çizgiler ince beyaz.
    <nav className="h-full min-h-screen bg-terakkiSurface border-r border-terakkiBorder w-60 flex flex-col dark:bg-white/[0.04] dark:border-white/[0.14]">
      {/* Marka: tanıtım sayfasına döner */}
      <Link to="/" title="Ana sayfa" aria-label="Terakki ana sayfa" className="flex items-center gap-2.5 px-4 h-16 border-b border-terakkiBorder hover:bg-terakkiSurfaceAlt transition-colors dark:border-white/[0.14] dark:hover:bg-white/[0.06]">
        {/* Tanıtım sitesiyle aynı marka: amblem, üstte "Adım Adım", altta "Terakki" */}
        <img src={dark ? amblemBeyaz : amblem} alt="" aria-hidden className="h-9 w-auto object-contain" />
        <BrandLockup size="sm" tone={dark ? "dark" : "light"} />
      </Link>

      {/* Bölümler */}
      <div className="flex-1 p-3 flex flex-col gap-1">
        {visibleSections.map((section) => (
          <div key={section.key || "home"} className="flex flex-col gap-1">
            {section.key ? (
              <div className="px-3 pt-4 pb-1 text-sm font-SemiBold tracking-wide text-terakkiNeutral dark:text-white/55">
                {t(section.key)}
              </div>
            ) : null}
            {section.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  [
                    "flex items-center gap-3 px-3 py-2 rounded-lg text-base transition-colors",
                    isActive
                      ? "bg-terakkiBrandCloud text-terakkiPrimaryDark font-SemiBold dark:bg-white/[0.12] dark:text-white"
                      : "text-terakkiPrimaryDark hover:bg-terakkiSurfaceAlt dark:text-white/80 dark:hover:bg-white/[0.08]",
                  ].join(" ")
                }
              >
                <item.Icon fontSize="small" aria-hidden />
                <span>{t(item.key)}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </div>

      {/* Kullanıcı */}
      <div className="p-3 border-t border-terakkiBorder dark:border-white/[0.14] flex items-center gap-2">
        <button
          type="button"
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={t("topbar_user_menu")}
          className="min-w-0 flex-1 flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-terakkiSurfaceAlt text-left dark:hover:bg-white/[0.08]"
        >
          <span className="w-9 h-9 shrink-0 rounded-full bg-terakkiPrimaryDark text-white flex items-center justify-center text-sm font-SemiBold dark:bg-white/[0.12]">
            {initial}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-SemiBold text-terakkiInk truncate dark:text-white">{displayName}</span>
            <span className="block text-xs text-terakkiNeutral truncate dark:text-white/70">{user?.email}</span>
          </span>
        </button>
        {/* Koyu / açık tema: simge geçilecek temayı gösterir (mobil yan menüdeki gibi) */}
        <button
          type="button"
          onClick={toggle}
          aria-label={dark ? "Açık tema" : "Koyu tema"}
          title={dark ? "Açık tema" : "Koyu tema"}
          className="w-10 h-10 p-0 leading-none shrink-0 rounded-full grid place-items-center bg-terakkiBrandCloud text-terakkiPrimaryDark hover:bg-terakkiBrandMist dark:bg-white/[0.1] dark:text-white dark:hover:bg-white/[0.16]"
        >
          {dark ? (
            <LightModeOutlinedIcon sx={{ fontSize: 20, display: "block", m: 0 }} aria-hidden />
          ) : (
            <DarkModeOutlinedIcon sx={{ fontSize: 20, display: "block", m: 0 }} aria-hidden />
          )}
        </button>
        <Menu
          open={Boolean(anchor)}
          anchorEl={anchor}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <MenuItem onClick={() => { setAnchor(null); navigate("/uygulama/hesap"); }}>
            <PersonOutlineIcon fontSize="small" className="mr-2" aria-hidden />
            {t("menu_profile")}
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <LogoutIcon fontSize="small" className="mr-2" aria-hidden />
            {t("nav_logout")}
          </MenuItem>
        </Menu>
      </div>
    </nav>
  );
}
